import { Divider, FormControl, IconButton, InputLabel, MenuItem, NativeSelect, Popover, Select, TextField, Tooltip, Typography } from '@material-ui/core'
import { Add, HelpOutline } from '@material-ui/icons';
import React, { useContext, useMemo, useState } from 'react'
import { GetClientCaseId } from '../../../../Functions/General';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Axios from 'axios';
import { FillContext } from '../../../../hooks/ContextStore';
import {
    CreateAnimals,
    CreateBankAccount,
    CreateBonus,
    CreateBrokerage,
    CreateBusiness,
    CreateCashOnHand,
    CreateChildren,
    CreateClub,
    CreateEquitableClaims,
    CreateFederalTax,
    CreateInsurance,
    CreateLiabilities,
    CreateMoney,
    CreateMsc,
    CreateOtherLiabilities,
    CreatePersonalProperty,
    CreatePropertyHeld,
    CreateRealProperty,
    CreateRetirment,
    CreateStocks,
    CreateTravelAward,
    CreateVehicle
} from '../Service/Request';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from "@date-io/moment";
import { useHistory } from "react-router-dom";
import InfoTooltip from './Tooltips';

function Create() {
    const client_case_id = GetClientCaseId();
    const [fill, setFill] = useContext(FillContext);
    const [date, setDate] = useState(new Date());
    const [anchorEl, setAnchorEl] = useState(null);
    const history = useHistory();

    const handlePopoverOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const estateList = useMemo(() => [
        {
            name: "Real Property",
            function: CreateRealProperty,
            condition: fill?.real_property || fill?.mineral_interest,
            link: "/estatev2/real-estate",
        },
        // {
        //     name: "Cash On Hand",
        //     function: CreateCashOnHand,
        //     condition: fill?.cash || fill?.equivalents,
        //     link: "/estatev2/cash-onhand",
        // },
        {
            name: "Bank Accounts",
            function: CreateBankAccount,
            condition: fill?.bank_account,
            link: "/estatev2/bank-accounts",
        },
        {
            name: "Brokerage",
            function: CreateBrokerage,
            condition: fill?.brokerage,
            link: "/estatev2/brokerage",
        },
        {
            name: "Stocks",
            function: CreateStocks,
            condition: fill?.public_stocks || fill?.employee_stock_option || fill?.employee_stock,
            link: "/estatev2/stocks",
        },
        {
            name: "Bonuses",
            function: CreateBonus,
            condition: fill?.bonuses,
            link: "/estatev2/bonuses",
        },
        {
            name: "Business Interests",
            function: CreateBusiness,
            condition: fill?.business_interest,
            link: "/estatev2/business-interests",
        },
        {
            name: "Retirement Accts & Benefits",
            function: CreateRetirment,
            condition:
                fill?.contribution_plan || fill?.benefit_plan || fill?.ira_sep || fill?.military_benefit ||
                fill?.non_qualified_plan || fill?.government_benefits || fill?.compensation_benefits || fill?.union_benefits,
            link: "/estatev2/retirement-benefits",
        },
        {
            name: "Insurance and Annuities",
            function: CreateInsurance,
            condition: fill?.life_insurance || fill?.annuities,
            link: "/estatev2/insurance-annuities",
        },
        {
            name: "Other Asset Types/Miscellaneous",
            function: CreateMsc,
            condition:
                fill?.intellectual_property || fill?.safe_deposit || fill?.storage_facilities ||
                fill?.contingent_assets || fill?.other_assets || fill?.precious_metals,
            link: "/estatev2/miscellaneous-property",
        },
        {
            name: "Vehicles",
            function: CreateVehicle,
            condition: fill?.vehicles,
            link: "/estatev2/vehicles",
        },
        {
            name: "Money Owed",
            function: CreateMoney,
            condition: fill?.money_owned,
            link: "/estatev2/money-owed",
        },
        {
            name: "Personal Property",
            function: CreatePersonalProperty,
            condition: fill?.client_possession || fill?.spouse_possession || fill?.spouse_both,
            link: "/estatev2/personal-property",
        },
        {
            name: "Animals",
            function: CreateAnimals,
            condition: fill?.pets || fill?.livestock,
            link: "/estatev2/livestock",
        },
        {
            name: "Club Memberships",
            function: CreateClub,
            condition: fill?.club_membership,
            link: "/estatev2/club-memberships",
        },
        {
            name: "Travel Award Benefits",
            function: CreateTravelAward,
            condition: fill?.travel_award_benefit,
            link: "/estatev2/travel",
        },
        {
            name: "Credit Cards & Revolving Debt",
            function: CreateLiabilities,
            condition:
                fill?.credit_card || fill?.federal_liabilities || fill?.contingent_liabilities ||
                fill?.amount_owned_to_attorney_or_professionals || fill?.other_liabilities,
            link: "/estatev2/credit-cards",
        },
        {
            name: "Federal and State Taxes",
            function: CreateFederalTax,
            condition:
                fill?.credit_card || fill?.federal_liabilities || fill?.contingent_liabilities ||
                fill?.amount_owned_to_attorney_or_professionals || fill?.other_liabilities,
            link: "/estatev2/federal-tax",
        },
        {
            name: "Other Debt",
            function: CreateOtherLiabilities,
            condition:
                fill?.credit_card || fill?.federal_liabilities || fill?.contingent_liabilities ||
                fill?.amount_owned_to_attorney_or_professionals || fill?.other_liabilities,
            link: "/estatev2/other-debt",
        },
        {
            name: "Children's Assets",
            function: CreateChildren,
            condition: fill?.children_assets,
            link: "/estatev2/child-accounts",
        },
        {
            name: "Property Held in Trust",
            function: CreatePropertyHeld,
            condition: fill?.asset_held_for_either_party || fill?.asset_held_for_benefit,
            link: "/estatev2/property-trust",
        },
        {
            name: "Equitable Claims",
            function: CreateEquitableClaims,
            condition: fill?.equitable_for_community_estate || fill?.equitable_for_separate_estate,
            link: "/estatev2/equitable-claims",
        },
    ], [fill]);

    const estateFillKeys = {
        "/estatev2/real-estate": ["real_property", "mineral_interest"],
        "/estatev2/cash-onhand": ["cash", "equivalents"],
        "/estatev2/bank-accounts": ["bank_account"],
        "/estatev2/brokerage": ["brokerage"],
        "/estatev2/stocks": ["public_stocks", "employee_stock_option", "employee_stock"],
        "/estatev2/bonuses": ["bonuses"],
        "/estatev2/business-interests": ["business_interest"],
        "/estatev2/retirement-benefits": [
            "contribution_plan",
            "benefit_plan",
            "ira_sep",
            "military_benefit",
            "non_qualified_plan",
            "government_benefits",
            "compensation_benefits",
            "union_benefits",
        ],
        "/estatev2/insurance-annuities": ["life_insurance", "annuities"],
        "/estatev2/miscellaneous-property": [
            "intellectual_property",
            "safe_deposit",
            "storage_facilities",
            "contingent_assets",
            "other_assets",
            "precious_metals",
        ],
        "/estatev2/vehicles": ["vehicles"],
        "/estatev2/money-owed": ["money_owned"],
        "/estatev2/personal-property": ["client_possession", "spouse_possession", "spouse_both"],
        "/estatev2/livestock": ["pets", "livestock"],
        "/estatev2/club-memberships": ["club_membership"],
        "/estatev2/travel": ["travel_award_benefit"],
        "/estatev2/credit-cards": [
            "credit_card",
            "federal_liabilities",
            "contingent_liabilities",
            "amount_owned_to_attorney_or_professionals",
            "other_liabilities",
        ],
        "/estatev2/federal-tax": [
            "credit_card",
            "federal_liabilities",
            "contingent_liabilities",
            "amount_owned_to_attorney_or_professionals",
            "other_liabilities",
        ],
        "/estatev2/other-debt": [
            "credit_card",
            "federal_liabilities",
            "contingent_liabilities",
            "amount_owned_to_attorney_or_professionals",
            "other_liabilities",
        ],
        "/estatev2/child-accounts": ["children_assets"],
        "/estatev2/property-trust": ["asset_held_for_either_party", "asset_held_for_benefit"],
        "/estatev2/equitable-claims": ["equitable_for_community_estate", "equitable_for_separate_estate"],
    };

    const updateFillWithKeys = (categoryLink) => {
        const keys = estateFillKeys[categoryLink] || [];
        if (keys.length === 0) return;
        // Create a new fill object copying current fill
        const newFill = { ...fill };

        keys.forEach((key) => {
            newFill[key] = 1;
        });
        setFill(newFill);
        //this will open the estate page with the selected category
        history.push(categoryLink);
    };

    const positions = [
        "Husband",
        "Wife",
        "Joint",
        "Client",
        "Spouse",
        "Third Party",
        "Other"
    ];

    const validationSchema = Yup.object({
        description: Yup.string().when('category', {
            is: (category) =>
                category === '/estatev2/bank-accounts',
            then: Yup.string()
                .matches(/^\d+$/, 'Bank Account must be numbers only')
                .required('Description is required'),
            otherwise: Yup.string().required('Description is required'),
        }),
        value: Yup.number().required('Value is required'),
        debt: Yup.number().required('Debt is required'),
        clientSP: Yup.string().required('Client SP is required'),
        spouseSP: Yup.string().required('Spouse SP is required'),
    });

    const formik = useFormik({
        initialValues: {
            description: '',
            value: '',
            debt: '',
            account_number: '',
            bankName: '',
            clientSP: '',
            spouseSP: '',
            date: date,
            category: '/estatev2/real-estate',
            type: 'checking',
            position: 'Husband',
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            values['date'] = date;
            const selected = estateList.find(estate => estate.link === formik.values.category);
            if (selected) {
                var response = await selected.function(values, client_case_id);
                debugger
                formik.resetForm({ values: { ...formik.initialValues, category: formik.values.category } }); //jsut to stay same assets based on scott
                history.push('/estatev2');
                updateFillWithKeys(selected.link);
            }
        },
    });

    const handleChange = (date) => {
        let date_dt = new Date(date);
        let new_date = new Date(date_dt.getFullYear(), date_dt.getMonth(), date_dt.getDate(), '10', '56', '00')
        setDate(new_date);
    };

    const labelHandler = (property) => {
        switch (property) {
            case 'description':
                switch (formik.values.category) {
                    case '/estatev2/bank-accounts':
                    case '/estatev2/credit-cards':
                    case '/estatev2/retirement-benefits':
                        return "Acct # (last 4 digits)";
                    case '/estatev2/real-estate':
                        return "Address";
                    case '/estatev2/stocks':
                        return "Name of Stock";
                    case '/estatev2/business-interests':
                        return "Business Name";
                    case '/estatev2/credit-cards':
                        return "Bank/CC name";
                    case '/estatev2/brokerage':
                        return "Financial Institution";
                    default:
                        return "Description";
                }
            case 'value':
                switch (formik.values.category) {
                    case '/estatev2/other-debt':
                    case '/estatev2/credit-cards':
                        return "Debt (if Applicable)";
                    case '/estatev2/federal-tax':
                        return "Overpayment (if applicable)";
                    default:
                        return "Value";
                }
            case 'debt':
                switch (formik.values.category) {
                    case '/estatev2/real-estate':
                        return "Mortgage Balance";
                    case '/estatev2/federal-tax':
                        return "Amount Owed";
                    default:
                        return "Debt (if Applicable)";
                }
            case 'clientSP':
                return "Client SP";
            case 'spouseSP':
                return "Spouse SP";
            default:
                return property.charAt(0).toUpperCase() + property.slice(1);
        }
    }

    const toolTipHandler = (property) => {
        switch (property) {
            case 'description':
                switch (formik.values.category) {
                    case '/estatev2/bank-accounts':
                        return "Enter the last 4 digits of the account number.";
                    case '/estatev2/credit-cards':
                        return "Enter the last 4 digits of the credit card number.";
                    case '/estatev2/personal-property':
                        return `You typically should not list EVERY item of personal property, but instead estimate the total value of groups of property, such as "Furniture in marital residence." An exception to this may be a particularly valuable item such as a particularly valuable painting or item of jewelry. When in doubt, ask your lawyer.`;
                    default:
                        return "Enter a description for this asset.";
                }
            case 'value':
                switch (formik.values.category) {
                    case '/estatev2/other-debt':
                    case '/estatev2/credit-cards':
                        return "Enter the total debt amount if applicable.";
                    case '/estatev2/business-interests':
                        return "You will often not know the value of a business. If you have a recent appraisal, or an estimate, please provide that and after you have clicked " + " click the notes icon next to the asset and explain how you arrived at the value.";
                    case '/estatev2/insurance-annuities':
                        return "For annuities, this estimated value is often provided on your regular statements. For life insurance, please provide the cash value (if any). This is not the amount the insurance would pay in the event of death, but the value you would receive if you cashed the policy out today (typically not applicable with TERM life policies).";
                    case '/estatev2/personal-property':
                        return "For physical property, the value will typically be the market value (what you could sell the present item for on the open market), as opposed to what the item would be worth new. When in doubt, ask your lawyer.";
                    default:
                        return "Enter the value of this asset.";
                }
            case 'debt':
                switch (formik.values.category) {
                    case '/estatev2/real-estate':
                        return "This is the balance of the first lien mortgage on the property. If there is a second lien on the property, list the second lien balance in the drop down below after you have clicked the " + " button to enter the asset.";
                    case '/estatev2/bank-accounts':
                        return "It would be rare for a bank account to have a debt associated with it. Place a value here only if the account is overdrawn.";
                    case '/estatev2/brokerage':
                        return "This will typically be applicable only if you have purchased stock on margin, participated in short selling, or other less common activities. When in doubt, ask your lawyer. ";
                    case '/estatev2/retirement-benefits':
                        return "If you have borrowed against your retirement, please put your loan balance here.";
                    default:
                        return "Any debt that is specifically attached and/or creating a lien on this particular asset. For many assets, this will not be applicable. ";
                }
            case 'bankName':
                return "Select the type of bank account.";
            case 'type':
                return "Select the type of bank name.";
            case 'position':
                return "This is the person who has physical possession of the asset, control of the asset, or in the case of a financial account, the person whose name is on the account. ";
            case 'clientSP':
                return "This is the value of your separate property interest in the asset. If the asset is wholly your separate property, this should equal the total value of the asset. You will need to ask your lawyer about the separate property laws in your state. ";
            case 'spouseSP':
                return "This is the value of your separate property interest in the asset. If the asset is wholly your separate property, this should equal the total value of the asset. You will need to ask your lawyer about the separate property laws in your state. ";
            default:
                return `Enter ${property} for this asset.`;
        }
    }

    return (
        <div className="card mt-2 shadow" style={{ position: 'relative' }}>
            <div className="card-body py-2">
                <form onSubmit={formik.handleSubmit}>
                    <div className="d-flex flex-wrap gap-1">
                        {/* Category */}
                        <div
                            className="d-flex flex-column"
                            style={{
                                minWidth: '180px',
                                marginRight: '20px',
                                marginTop: '4px'
                            }}
                        >
                            <div className="d-flex align-items-center" style={{ minWidth: '160px', marginLeft: '-20px' }}>
                                <InfoTooltip link={formik.values.category} />
                                <label className="form-label text-muted small mb-1" >Category</label>
                            </div>
                            <Select
                                size="small"
                                name="category"
                                value={formik.values.category}
                                onChange={formik.handleChange}
                                error={formik.touched.category && formik.errors.category}
                                helperText={formik.touched.category && formik.errors.category}
                            >
                                {estateList?.map((estate) => (
                                    <MenuItem key={estate.link} value={estate.link}>
                                        {estate.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>

                        {/* Bank Name */}
                        {formik.values.category === '/estatev2/bank-accounts' && (
                            <div className="d-flex align-items-center" style={{ minWidth: '160px', }}>
                                <Tooltip title={toolTipHandler('bankName')} className='mr-1 ml-3'>
                                    <HelpOutline fontSize="small" />
                                </Tooltip> 
                                <TextField
                                    size="small"
                                    label="Bank Name"
                                    name="bankName"
                                    value={formik.values.bankName}
                                    onChange={formik.handleChange}
                                    error={formik.touched.bankName && formik.errors.bankName}
                                    helperText={formik.touched.bankName && formik.errors.bankName}
                                />
                            </div>
                        )}

                        {/* Credit Card Name */}
                        {formik.values.category === '/estatev2/credit-cards' && (
                            <div className="d-flex align-items-center" style={{ minWidth: '160px', }}>
                                <Tooltip title={toolTipHandler('bankName')} className='mr-1 ml-3'>
                                    <HelpOutline fontSize="small" />
                                </Tooltip> 
                                <TextField
                                    size="small"
                                    label="Name"
                                    name="bankName"
                                    value={formik.values.bankName}
                                    onChange={formik.handleChange}
                                    error={formik.touched.bankName && formik.errors.bankName}
                                    helperText={formik.touched.bankName && formik.errors.bankName}
                                />
                            </div>
                        )}
                           {/* Credit Card Name */}
                        {formik.values.category === '/estatev2/retirement-benefits' && (
                            <div className="d-flex align-items-center" style={{ minWidth: '160px', }}>
                                <Tooltip title={toolTipHandler('bankName')} className='mr-1 ml-3'>
                                    <HelpOutline fontSize="small" />
                                </Tooltip> 
                                <TextField
                                    size="small"
                                    label="Name"
                                    name="bankName"
                                    value={formik.values.bankName}
                                    onChange={formik.handleChange}
                                    error={formik.touched.bankName && formik.errors.bankName}
                                    helperText={formik.touched.bankName && formik.errors.bankName}
                                />
                            </div>
                        )}

                        {/* Description */}
                        <div className="d-flex align-items-center" style={{ minWidth: '180px' }}>
                            <Tooltip title={toolTipHandler('description')} className='mr-1 ml-3'>
                                <HelpOutline fontSize="small" />
                            </Tooltip> <TextField
                                size="small"
                                label={labelHandler('description')}
                                name="description"
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                error={formik.touched.description && formik.errors.description}
                                helperText={formik.touched.description && formik.errors.description}
                            />
                        </div>
                        {/* Value */}
                        <div className="d-flex align-items-center" style={{ minWidth: '80px' }}>
                            <Tooltip title={toolTipHandler('value')} className='mr-1 ml-3'>
                                <HelpOutline fontSize="small" />
                            </Tooltip> <TextField
                                size="small"
                                label={labelHandler('value')}
                                name="value"
                                value={formik.values.value}
                                onChange={formik.handleChange}
                                error={formik.touched.value && formik.errors.value}
                                helperText={formik.touched.value && formik.errors.value}
                            />
                        </div>
                        {/* Debt */}
                        {!(
                            formik.values.category === '/estatev2/other-debt' ||
                            formik.values.category === '/estatev2/credit-cards'
                        ) && (
                                <div className="d-flex align-items-center" style={{ minWidth: '160px', }}>
                                    <Tooltip title={toolTipHandler('debt')} className='mr-1 ml-3'>
                                        <HelpOutline fontSize="small" />
                                    </Tooltip>
                                    <TextField
                                        size="small"
                                        label={labelHandler('debt')}
                                        name="debt"
                                        value={formik.values.debt}
                                        onChange={formik.handleChange}
                                        error={formik.touched.debt && formik.errors.debt}
                                        helperText={formik.touched.debt && formik.errors.debt}
                                    />
                                </div>
                            )}


                        {/* Date */}
                        <div style={{ minWidth: '180px', marginTop: '-4px' }}>
                            <MuiPickersUtilsProvider utils={MomentUtils}>
                                <KeyboardDatePicker
                                    className="w-100"
                                    size="small"
                                    disableToolbar
                                    variant="inline"
                                    format="MM/DD/yyyy"
                                    margin="normal"
                                    id="date-picker-inline"
                                    value={date}
                                    name="date"
                                    onChange={handleChange}
                                    KeyboardButtonProps={{
                                        'aria-label': 'change date',
                                    }}
                                />
                            </MuiPickersUtilsProvider>
                        </div>

                        {/* Type Select */}
                        {formik.values.category === '/estatev2/bank-accounts' && (
                            <div className="d-flex flex-column mr-6 ml-4" style={{ minWidth: '180px', }}>
                                <div className="d-flex align-items-center" style={{ minWidth: '160px', marginLeft: '-20px' }} >
                                    <Tooltip title={toolTipHandler('type')} className='mr-1 ml-3'>
                                        <HelpOutline fontSize="small" />
                                    </Tooltip>  <label className="form-label text-muted small mb-1">Type</label>
                                </div>
                                <Select
                                    size="small"
                                    name="type"
                                    value={formik.values.type}
                                    onChange={formik.handleChange}
                                    error={formik.touched.type && formik.errors.type}
                                    helperText={formik.touched.type && formik.errors.type}
                                >
                                    <MenuItem value="checking">checking</MenuItem>
                                    <MenuItem value="savings">savings</MenuItem>
                                    <MenuItem value="money market">money market</MenuItem>
                                    <MenuItem value="certificate of deposit">certificate of deposit</MenuItem>
                                </Select>
                            </div>
                        )}

                        {/* Position Select */}
                        <div className="d-flex flex-column" style={{ minWidth: '180px', }}>
                            <div className="d-flex align-items-center" style={{ minWidth: '160px', marginLeft: '-23px' }} >
                                <Tooltip title={toolTipHandler('position')} className='mr-2 ml-3'>
                                    <HelpOutline fontSize="small" />
                                </Tooltip>

                                <label className="form-label text-muted small mb-1">Name/Control of</label>
                            </div>
                            <Select
                                size="small"
                                name="position"
                                value={formik.values.position}
                                onChange={formik.handleChange}
                                error={formik.touched.position && formik.errors.position}
                                helperText={formik.touched.position && formik.errors.position}
                            >
                                {positions.map((position) => (
                                    <MenuItem key={position} value={position}>
                                        {position}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>

                        {/* Client SP */}
                        <div className="d-flex align-items-center" style={{ minWidth: '140px' }}>
                            <Tooltip title={toolTipHandler('clientSP')} className='mr-1 ml-3'>
                                <HelpOutline fontSize="small" />
                            </Tooltip><TextField
                                size="small"
                                label="Client SP"
                                name="clientSP"
                                value={formik.values.clientSP}
                                onChange={formik.handleChange}
                                error={formik.touched.clientSP && formik.errors.clientSP}
                                helperText={formik.touched.clientSP && formik.errors.clientSP}
                            />
                        </div>

                        {/* Spouse SP */}
                        <div className="d-flex align-items-center" style={{ minWidth: '140px' }}>
                            <Tooltip title={toolTipHandler('spouseSP')} className='mr-1 ml-3'>
                                <HelpOutline fontSize="small" />
                            </Tooltip> <TextField
                                size="small"
                                label="Spouse SP"
                                name="spouseSP"
                                value={formik.values.spouseSP}
                                onChange={formik.handleChange}
                                error={formik.touched.spouseSP && formik.errors.spouseSP}
                                helperText={formik.touched.spouseSP && formik.errors.spouseSP}
                            />
                        </div>
                    </div>
                </form>
            </div>

            {/* Submit button pinned bottom right */}
            <div
                style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '20px',
                }}
            >
                <IconButton
                    aria-label="submit"
                    type="submit"
                    style={{ background: '#007bff', color: '#fff' }}
                    onClick={formik.handleSubmit}
                >
                    <Add fontSize="small" />
                </IconButton>
            </div>
        </div>
    );
}

export default Create